import dayjs, { type Dayjs } from 'dayjs';
import React, { useEffect, useState } from 'react';

import { updateStateAttribute } from '../helpers/helper';
import { useScroll } from '../hooks/useScroll';
import CLocalizedText from './CLocalizedText';
import CustomDatePicker from './CustomDatePicker';

interface SingleDateSelectorProps {
  label: string;
  minDate: number;
  maxDate: number;
  initDate: number;
  onChange: (dateUnixMs: number) => void;
  limitWidth?: boolean;
  shouldDisableDate?: (date: Dayjs) => boolean;
}

interface State {
  date?: Dayjs;
  errorMessage?: JSX.Element;
}

export default function SingleDateSelector(props: SingleDateSelectorProps) {
  const [state, setState] = useState<State>({
    date: dayjs(props.initDate)
  });

  const [open, setOpen] = useState(false);

  const { scrollY } = useScroll();

  useEffect(() => {
    if (open) {
      setOpen(false);
    }
  }, [scrollY]);

  const validateDate = (unixMs: number) => {
    let errorMessage: JSX.Element | undefined;

    const minDateText = dayjs(props.minDate).format('DD MMM YYYY');
    const maxDateText = dayjs(props.maxDate).format('DD MMM YYYY');

    if (dayjs(unixMs).isBefore(props.minDate, 'day')) {
      errorMessage = (
        <span className={'field-error-message'}>
          <CLocalizedText inline dictKey={'errorInitialDate'} />
          &nbsp;{minDateText}
          <CLocalizedText dictKey={'errorEndDateIsAfter2'} />
        </span>
      );
    }

    if (dayjs(unixMs).isAfter(props.maxDate, 'day')) {
      errorMessage = (
        <span className={'field-error-message'}>
          <CLocalizedText inline dictKey={'errorEndDate'} />
          &nbsp;{maxDateText}
          <CLocalizedText dictKey={'errorEndDateIsAfter2'} />
        </span>
      );
    }

    updateStateAttribute<State>({ errorMessage }, setState);
    return !errorMessage;
  };

  const handleChange = (value: Dayjs) => {
    if (!value?.isValid()) return;

    const dayAtMidnight = value.set('hour', 0).set('minute', 0).set('second', 0);
    const unixMs = dayAtMidnight.unix() * 1000;

    updateStateAttribute<State>({ date: value }, setState);

    if (!validateDate(unixMs)) return;

    setOpen(false);
    props.onChange(unixMs);
  };

  useEffect(() => {
    updateStateAttribute<State>(
      {
        date: dayjs(props.initDate),
        errorMessage: undefined
      },
      setState
    );

    validateDate(props.initDate);
  }, [props.initDate, props.minDate, props.maxDate]);

  return (
    <div
      className={'flex flex-col'}
      style={{
        width: props.limitWidth ? '250px' : '100%'
      }}
    >
      <CustomDatePicker
        label={props.label}
        value={state.date}
        minDate={dayjs(props.minDate)}
        maxDate={dayjs(props.maxDate)}
        isError={!!state.errorMessage}
        open={open}
        onOpen={() => {
          setOpen(true);
        }}
        onClose={() => {
          setOpen(false);
        }}
        shouldDisableDate={props.shouldDisableDate}
        onChange={handleChange}
      />

      {state.errorMessage ? <div className={'text-red-600 text-[0.75rem] mt-1 ml-1'}>{state.errorMessage}</div> : null}
    </div>
  );
}
